import {css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo} from 'react';
import ReactFlow, {ReactFlowInstance, useEdgesState, useNodesState, Node, Edge, OnConnect} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  HandlesContext,
  HandlesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';

import {getEdgeTypeFromHandleId} from './flowchartIdentifierUtils';
import {
  READ_ONLY_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  DEFAULT_FLOWCHART_PROPS,
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES,
  ProcessFlowchartEdgeType,
} from './TransitionModalWithFlowchartEditorConstants';
import {
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_NODE_TYPES,
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_EDGE_TYPES,
} from './TransitionModalWithFlowchartEditorRegistry';

interface TransitionModalWithFlowchartEditorBodyProps {
  initialNodes: Array<Node>;
  initialEdges: Array<Edge>;
  disabled?: boolean;
  onChange: ({nodes, edges}: {nodes: Array<Node>; edges: Array<Edge>}) => void;
}

export default function TransitionModalWithFlowchartEditorBody(
  props: TransitionModalWithFlowchartEditorBodyProps
): JSX.Element {
  const [nodes, _, onNodesChange] = useNodesState(props.initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(props.initialEdges);
  const interactionProps = props.disabled ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;

  const onConnect: OnConnect = useCallback(
    (params) => {
      if (params.sourceHandle == null || params.targetHandle == null) {
        return;
      }
      const sourceHandleEdgeType = getEdgeTypeFromHandleId(params.sourceHandle);
      const targetHandleEdgeType = getEdgeTypeFromHandleId(params.targetHandle);
      if (sourceHandleEdgeType !== targetHandleEdgeType) {
        return;
      }

      const edgeAlreadyExists = edges.some(
        (edge) =>
          edge.source === params.source &&
          edge.target === params.target &&
          edge.sourceHandle === params.sourceHandle &&
          edge.targetHandle === params.targetHandle
      );

      if (edgeAlreadyExists) {
        return;
      }

      const dataEdgeForTargetFieldExists = edges.some(
        (edge) =>
          edge.target === params.target &&
          edge.targetHandle === params.targetHandle &&
          edge.type === ProcessFlowchartEdgeType.DATA_FLOW
      );
      if (dataEdgeForTargetFieldExists) {
        return;
      }

      const newEdge = {
        id: uuidv4(),
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: sourceHandleEdgeType,
      };

      setEdges((eds) => [...eds, newEdge]);
    },
    [edges, setEdges]
  );

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      handlesInUse: edges.flatMap((edge) => {
        if (!edge.sourceHandle || !edge.targetHandle) {
          throw new Error('Invalid edge: missing sourceHandle or targetHandle');
        }
        return [edge.sourceHandle, edge.targetHandle];
      }),
    }),
    [edges]
  );

  useEffect(() => {
    props.onChange({nodes, edges});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  return (
    <div className={css(TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES.reactFlowCanvas)}>
      <ReactFlowWrapper fullWidth={true}>
        <HandlesContext.Provider value={handlesContext}>
          <ReactFlow
            {...DEFAULT_FLOWCHART_PROPS}
            {...interactionProps}
            nodes={nodes}
            nodeTypes={TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_NODE_TYPES}
            edgeTypes={TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_EDGE_TYPES}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onInit={(reactFlowInstance: ReactFlowInstance) => {
              reactFlowInstance.fitView();
            }}
            onConnect={onConnect}
          >
            <HorizontalControls />
            <DefaultBackground />
          </ReactFlow>
        </HandlesContext.Provider>
      </ReactFlowWrapper>
    </div>
  );
}
